import moment from 'moment';
import { RuleDetailsQueryData } from '../../../../queries/types';
import { RuleDetails } from 'src/products/connector/types/ruleTypes';
import { useEffect, useState } from 'react';
import { useDataValidation } from '../../../../hooks/useDataValidation';

const RliStatus = [
  'FAIL',
  'INACTIVE',
  'ACTIVE',
  'SUCCESS',
  'CREATING',
] as const;

export type TestStatus = (typeof RliStatus)[number];

const convertToRuleItemStatus = (
  status?: TestStatus | null
): RuleDetails['status'] => {
  if (!status) {
    return 'Scheduled';
  }
  switch (status) {
    case 'FAIL':
      return 'Failed';
    case 'CREATING':
    case 'INACTIVE':
    case 'ACTIVE':
      return 'Scheduled';
    case 'SUCCESS':
      return 'Success';
    default:
      return 'Scheduled';
  }
};

export const determineFailedRecordCountText = (
  status: TestStatus | null,
  anomalousRecords: number
) => {
  switch (status) {
    case 'FAIL':
      return anomalousRecords;
    case 'SUCCESS':
      return 0;
    default:
      return '--';
  }
};

export const useGetDataValidationResults = (ruleItem: RuleDetailsQueryData) => {
  if (
    !ruleItem ||
    !ruleItem.checkId ||
    !ruleItem.datastoreId ||
    !ruleItem.containerId
  ) {
    throw new Error('Rule does not have required fields');
  }

  const {
    ruleDetails: apiRuleDetails,
    fetchRuleDetails,
    detailsLoading,
  } = useDataValidation();
  const [data, setData] = useState<any | null>(null);

  const ruleId = parseInt(ruleItem.checkId.toString(), 10);
  const tableId = parseInt(ruleItem.containerId.toString(), 10);
  const datastoreId = parseInt(ruleItem.datastoreId.toString(), 10);

  useEffect(() => {
    fetchRuleDetails(ruleId, tableId, datastoreId);
  }, [ruleId, tableId, datastoreId, fetchRuleDetails]);

  useEffect(() => {
    if (apiRuleDetails && !detailsLoading) {
      setData(apiRuleDetails.data);
    }
  }, [apiRuleDetails, detailsLoading]);

  if (!data) {
    return null;
  }

  const failedRecordCount = determineFailedRecordCountText(
    ruleItem.status as TestStatus,
    data.anomalousRecords || 0
  );

  let keys: string[] = [];
  const failedRecords =
    data.failedSourceRecords?.map((record: any, idx: number) => ({
      ...record,
      _frontend_id: idx.toString(),
    })) || [];

  if (Array.isArray(failedRecords) && failedRecords.length > 0) {
    keys = Object.keys(failedRecords[0]).filter(
      (key) =>
        ![
          'anomaly_uuid',
          '_qualytics_source_partition',
          '_frontend_id',
        ].includes(key)
    );
  }

  const ruleDetails: RuleDetails = {
    id: ruleId.toString() ?? '',
    anomalyID: data.anomalyID || 0,
    lastRun: moment().format('MMM DD, YYYY h:mm A'),
    failedRecordCount: failedRecordCount as number,
    failedSourceRecords: {
      records: failedRecords,
      columns: keys,
      failedColumns: data.failedColumns || [],
    },
    status: convertToRuleItemStatus(ruleItem.status as TestStatus),
    ruleName: ruleItem.displayName ?? '',
    tableRecordCount: data.tableRecordCount || 0,
    canEdit: false,
    failedRowCountHistory: data.failedRowCountData || [],
    parameterValues: [],
    testDefinition: {
      description: '',
      type: '',
      name: '',
    },
    entityLink: '',
  };

  return ruleDetails;
};
